import type React from 'react';
import { create } from 'zustand';

import type { InspectorBreadcrumbItem, Presentation, Subject } from '../types';

type StudioPanelRenderer = (subject: Subject) => React.ReactNode | null;

interface InspectorState {
  /**
   * Stack of open subjects. Length 0 means closed. Length 1 is the typical
   * modal use. Length > 1 means the drawer has been pushed onto multiple
   * times (open a post → click an image inside it → push image onto stack).
   */
  stack: Subject[];
  /** Effective presentation for the current top of the stack. */
  presentation: Presentation;
  /** Optional chrome metadata for page-like workspace presentations. */
  breadcrumbs: InspectorBreadcrumbItem[];
  /** Convenience flag — true when stack has at least one subject. */
  open: boolean;
  /**
   * Phase 5 (PJ-1): when set, the body's header renders a back-arrow chip
   * pointing at the named project. Cleared on close. Populated by callers
   * that opened the inspector from a project context (e.g. /projects/:id
   * → click post → backToProjectId === ":id").
   */
  backToProjectId: string | null;
  /** Open with a new subject. Clears any existing stack. */
  openSubject: (
    subject: Subject,
    opts?: {
      presentation?: Presentation;
      backToProjectId?: string | null;
      breadcrumbs?: InspectorBreadcrumbItem[];
    },
  ) => void;
  /** Push onto the existing stack. Drawer mode only — modal mode replaces. */
  push: (subject: Subject) => void;
  /** Pop the top of the stack. If stack becomes empty, also closes. */
  pop: () => void;
  /** Close everything. */
  close: () => void;
  /**
   * Optional studio-panel renderer. When set, the global Inspector mount
   * will invoke this for the current top-of-stack subject and pass the
   * returned node through PostBody's `studioPanel` slot. Mirrors the
   * `setPostFocusRenderer` pattern in `useArtifactStore` — keeps studio
   * logic in `/create` while the Inspector primitive stays design-pure.
   */
  studioPanelRenderer: StudioPanelRenderer | null;
  setStudioPanelRenderer: (fn: StudioPanelRenderer | null) => void;
}

/**
 * Global Inspector store. Replaces `useImageDialogStore` and `useVideoDialogStore`
 * (deletions land in PR 4). PR 1 ships the store with no production callers —
 * the style guide showcase exercises it.
 *
 * Spec: docs/superpowers/specs/2026-04-26-inspector-primitive-and-surface-unification-design.md
 */
export const useInspector = create<InspectorState>()((set) => ({
  stack: [],
  presentation: 'modal',
  breadcrumbs: [],
  open: false,
  backToProjectId: null,
  openSubject: (subject, opts) =>
    set({
      stack: [subject],
      presentation: opts?.presentation ?? 'modal',
      breadcrumbs: opts?.breadcrumbs ?? [],
      open: true,
      backToProjectId: opts?.backToProjectId ?? null,
    }),
  push: (subject) =>
    set((state) => ({
      stack: [...state.stack, subject],
      open: true,
    })),
  pop: () =>
    set((state) => {
      const next = state.stack.slice(0, -1);
      return {
        stack: next,
        open: next.length > 0,
        breadcrumbs: next.length > 0 ? state.breadcrumbs : [],
        backToProjectId: next.length > 0 ? state.backToProjectId : null,
      };
    }),
  close: () => set({ stack: [], breadcrumbs: [], open: false, backToProjectId: null }),
  studioPanelRenderer: null,
  setStudioPanelRenderer: (fn) => set({ studioPanelRenderer: fn }),
}));
